import type { Table } from "dexie";
import { DateTime } from "luxon";

import { BaseDexieRepository } from "../base-repository";
import type { LocalAudioRecordingBlobRecord } from "@/domains/dexie-db/models";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";

export type UpsertLocalAudioRecordingBlobData = Omit<
  LocalAudioRecordingBlobRecord,
  "id" | "associatedAudioRecordingId" | "blobIndex" | "createdAt" | "updatedAt"
>;

const sortByBlobIndex = (
  left: LocalAudioRecordingBlobRecord,
  right: LocalAudioRecordingBlobRecord
): number => left.blobIndex - right.blobIndex;

export class LocalAudioRecordingBlobsRepository extends BaseDexieRepository<LocalAudioRecordingBlobRecord> {
  constructor({ tableRef }: { tableRef: () => Table<LocalAudioRecordingBlobRecord, number> }) {
    super({ tableRef });
  }

  async upsert({
    associatedAudioRecordingId,
    blobIndex,
    data,
  }: {
    associatedAudioRecordingId: Uuid;
    blobIndex: number;
    data: UpsertLocalAudioRecordingBlobData;
  }): Promise<void> {
    await this.transaction("rw", async () => {
      await this.table.where({ associatedAudioRecordingId, blobIndex }).delete();

      const id = uuidModule.resolveLocalAudioRecordingBlobId({
        associatedAudioRecordingId,
        blobIndex,
      });

      await this.table.add({
        id,
        associatedAudioRecordingId,
        blobIndex,
        ...data,
        createdAt: DateTime.now(),
        updatedAt: DateTime.now(),
      });
    });
  }

  async update({
    associatedAudioRecordingId,
    blobIndex,
    data,
  }: {
    associatedAudioRecordingId: Uuid;
    blobIndex: number;
    data: Partial<UpsertLocalAudioRecordingBlobData>;
  }): Promise<void> {
    await this.table
      .where({ associatedAudioRecordingId, blobIndex })
      .modify({ ...data, updatedAt: DateTime.now() });
  }

  async getAllByRecordingId({
    associatedAudioRecordingId,
  }: {
    associatedAudioRecordingId: Uuid;
  }): Promise<LocalAudioRecordingBlobRecord[]> {
    const records = await this.table.where({ associatedAudioRecordingId }).toArray();
    return records.sort(sortByBlobIndex);
  }

  async getPendingByRecordingId({
    associatedAudioRecordingId,
  }: {
    associatedAudioRecordingId: Uuid;
  }): Promise<LocalAudioRecordingBlobRecord[]> {
    const records = await this.table.where({ associatedAudioRecordingId }).toArray();
    return records.filter(record => !record.isUploaded).sort(sortByBlobIndex);
  }

  async deleteAllByRecordingId({
    associatedAudioRecordingId,
  }: {
    associatedAudioRecordingId: Uuid;
  }): Promise<void> {
    await this.table.where({ associatedAudioRecordingId }).delete();
  }
}
